import {
    NOTIFY_MULTIPLE_FROM_WORKER,
    UPLOAD_WORKER_UNKNOWN_ERROR,
    useAppDispatch,
    useAppState
} from "../../app/AppContext";
import {useCallback, useEffect} from "react";
import {clientLog} from "../../clientLog";
import {kickOffUploadWorker} from "../../webworkers/fileuploads/kickOffUploadWorker";
import {getWorkerConfigBase} from "../../webworkers/getWorkerConfigBase";
import {refreshToken} from "@ekultur/authentication";

/**
 * Listener used to listen for, and parse, messages sent by the upload web worker.
 *
 * @return {React.JSX.Element} An empty JSX element.
 */
export const UploadWorkerListener = () => {
    const appDispatch = useAppDispatch();
    const {operations} = useAppState();

    const isValidMessage = event => {
        if (!event) {
            return;
        }

        if (!event.data) {
            return false;
        }

        const {channel, action} = event.data;
        return !(!channel || channel !== 'client' || !action || action === '');
    };

    const workerMessageParser = useCallback((event) => {
        if (!isValidMessage(event)) {
            return;
        }

        const data = event.data;
        const {action} = data
        const {data: statusData} = data;

        if (action === 'statusUpdate') {
            clientLog('debug', () => `uploadworker statusupdate: ${JSON.stringify(event.data)}`, 'uploadworker');
            // Notify the GUI about file upload status changes.
            appDispatch({
                type: NOTIFY_MULTIPLE_FROM_WORKER,
                messages: statusData
            });
        } else if (action === 'error') {
            clientLog('error', `uploadworker error: ${JSON.stringify(event.data.message)}`, 'uploadworker');
            appDispatch({type: UPLOAD_WORKER_UNKNOWN_ERROR});
        } else if (action === 'expiredToken') {
            clientLog('warn', 'uploadworker: token expired — refreshing before restarting worker', 'uploadworker');

            const apiGateway = window._env_.REACT_APP_DAMS_ADMIN_API;

            // Refresh the access token first so getWorkerConfigBase picks up fresh
            // credentials from localStorage, then restart the upload worker.
            refreshToken({
                apiGateway: window._env_.REACT_APP_API_GATEWAY,
                onSuccess: () => {
                    clientLog('info', 'uploadworker: token refreshed — restarting worker', 'uploadworker');
                    for (let i = 0, max = operations.length; i < max; i++) {
                        const operation = operations[i];
                        kickOffUploadWorker({
                            workerConfig: getWorkerConfigBase(apiGateway),
                            operationId: operation['jobId'],
                            operations: operations.filter(o => o.jobType === 'FILE_UPLOAD'),
                            uploadWorkerDispatch: appDispatch
                        });
                    }
                },
            });
        } else {
            clientLog('warn', `uploadworker sent unknown message: ${JSON.stringify(event.data)}`, 'uploadworker');
        }
    }, [appDispatch, operations]);

    /**
     * Hook that listens for messages from the upload worker.
     */
    useEffect(() => {
        window._uploadWorkerInstance.onmessage = workerMessageParser;
        return () => {
            // Detach the handler so the previous closure (capturing stale
            // `operations`) is released and cannot fire after unmount.
            window._uploadWorkerInstance.onmessage = null;
        };
    }, [workerMessageParser]);

    return <></>;
};